<template>
  <div class="settings-org-section">
    <SettingsSectionHeader
      title="Account"
      subtitle="Your profile, access, and notification settings"
      icon="pi-user"
    >
      <template
        v-if="statusLabel"
        #actions
      >
        <span
          class="settings-account-section__status"
          :class="statusActive
            ? 'settings-account-section__status--active'
            : 'settings-account-section__status--inactive'"
        >
          <span class="settings-account-section__status-dot" aria-hidden="true" />
          {{ statusLabel }}
        </span>
      </template>
    </SettingsSectionHeader>

    <div class="settings-org-section__body">
      <div
        v-if="loading"
        class="settings-org-section__state"
      >
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <template v-else-if="user.isLogged">
        <section
          class="settings-account-section__block"
          aria-labelledby="settings-account-profile-heading"
        >
          <h3
            id="settings-account-profile-heading"
            class="settings-account-section__heading"
          >
            Profile
          </h3>
          <div class="settings-account-section__grid">
            <div class="settings-account-section__field">
              <span class="settings-account-section__label">Name</span>
              <p class="settings-account-section__value">{{ user.name || '—' }}</p>
            </div>
            <div class="settings-account-section__field">
              <span class="settings-account-section__label">Account email</span>
              <p class="settings-account-section__value settings-account-section__value--break">
                {{ user.email || '—' }}
              </p>
            </div>
          </div>
        </section>

        <section
          class="settings-account-section__block"
          aria-labelledby="settings-account-org-heading"
        >
          <h3
            id="settings-account-org-heading"
            class="settings-account-section__heading"
          >
            Organization
          </h3>
          <div class="settings-account-section__org-grid">
            <div
              v-if="user.departmentId != null"
              class="settings-account-section__org-card"
            >
              <div>
                <span class="settings-account-section__label">Department</span>
                <p class="settings-account-section__hint">Assigned department</p>
              </div>
              <Tag
                :value="departmentName"
                class="text-xs font-semibold shrink-0"
                :style="departmentTagStyle"
              />
            </div>

            <div
              class="settings-account-section__org-card settings-account-section__org-card--roles"
              :class="{ 'settings-account-section__org-card--wide': user.departmentId == null }"
            >
              <span class="settings-account-section__label">Roles</span>
              <p class="settings-account-section__hint">Access levels for this account</p>
              <div
                v-if="user.roles && user.roles.length"
                class="settings-account-section__tags"
              >
                <Tag
                  v-for="role in user.roles"
                  :key="role.id || role.code"
                  :value="getRoleDisplayName(role.code || role.name)"
                  :severity="getRoleSeverity(role.code)"
                  class="text-xs"
                />
              </div>
              <p
                v-else
                class="settings-account-section__empty-text"
              >
                No roles assigned
              </p>
            </div>
          </div>
        </section>

        <section
          class="settings-account-section__block"
          aria-labelledby="settings-account-notify-heading"
        >
          <h3
            id="settings-account-notify-heading"
            class="settings-account-section__heading"
          >
            Notifications
          </h3>
          <TelegramUserSettings
            :http-client="axiosInstance"
            telegram-account-path="telegram/account"
            :show-timezone="true"
            fallback-telegram-url="https://t.me/foxnotify_bot"
          />
        </section>

        <p class="settings-account-section__footer">
          Roles and department are managed by administrators.
        </p>
      </template>

      <div
        v-else
        class="settings-org-section__empty"
      >
        <p class="font-semibold">You're not logged in</p>
        <p>Sign in to view your account and link Telegram notifications.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import ProgressSpinner from 'primevue/progressspinner';
import Tag from 'primevue/tag';
import { fetchDepartmentsList } from '@/api/departmentsApi';
import { getDepartmentTagStyle, getRoleSeverity, getRoleDisplayName } from '@/utils/tagColors';
import axiosInstance from '@/utils/axios';
import TelegramUserSettings from '@/components/account/TelegramUserSettings.vue';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';

defineOptions({ name: 'SettingsAccountSection' });

defineProps({
  section: {
    type: Object,
    default: null,
  },
});

defineEmits(['close-settings']);

const store = useStore();
const user = computed(() => store.state.users?.user ?? {});
const loading = ref(true);
const departments = ref([]);

const statusActive = computed(() => user.value?.status === 1 || user.value?.status === '1');
const statusLabel = computed(() => {
  if (!user.value?.isLogged) return '';
  return statusActive.value ? 'Active' : 'Inactive';
});

const departmentName = computed(() => {
  const id = user.value?.departmentId;
  if (id == null) return null;
  const d = departments.value.find((dept) => Number(dept.id) === Number(id));
  return d?.name ?? `Department #${id}`;
});

const currentDepartment = computed(() => {
  const id = user.value?.departmentId;
  if (id == null) return null;
  return departments.value.find((dept) => Number(dept.id) === Number(id)) ?? null;
});

const departmentTagStyle = computed(() => {
  const id = user.value?.departmentId;
  if (id == null) return {};
  const d = currentDepartment.value;
  return getDepartmentTagStyle(
    id,
    departmentName.value,
    d?.tag_color ?? null,
    d?.tag_text_color ?? null
  );
});

onMounted(async () => {
  if (user.value?.isLogged) {
    try {
      departments.value = await fetchDepartmentsList({ active: 'all' });
    } catch (_) {
      departments.value = [];
    }
  }
  loading.value = false;
});
</script>

<style scoped>
.settings-account-section__status {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid transparent;
}

.settings-account-section__status--active {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
  border-color: rgba(16, 185, 129, 0.3);
}

.settings-account-section__status--inactive {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
  border-color: rgba(245, 158, 11, 0.3);
}

:global(.dark) .settings-account-section__status--active {
  background: rgba(52, 211, 153, 0.1);
  color: #6ee7b7;
  border-color: rgba(52, 211, 153, 0.3);
}

:global(.dark) .settings-account-section__status--inactive {
  background: rgba(251, 191, 36, 0.1);
  color: #fcd34d;
  border-color: rgba(251, 191, 36, 0.3);
}

.settings-account-section__status-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: currentColor;
}

.settings-account-section__block {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.settings-account-section__heading {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--crc-text-muted, var(--text-color-secondary));
}

.settings-account-section__grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 0.75rem;
}

@media (min-width: 640px) {
  .settings-account-section__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.settings-account-section__field {
  border: 1px solid var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  padding: 0.875rem 1rem;
  background: var(--crc-bar-bg, var(--surface-card));
}

.settings-account-section__label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--crc-text-muted, var(--text-color-secondary));
}

.settings-account-section__value {
  margin: 0.375rem 0 0;
  font-size: 0.9375rem;
  color: var(--crc-text, var(--text-color));
  word-break: break-word;
}

.settings-account-section__value--break {
  overflow-wrap: anywhere;
}

.settings-account-section__org-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 0.75rem;
}

@media (min-width: 768px) {
  .settings-account-section__org-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.settings-account-section__org-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 7rem;
  border: 1px solid var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  padding: 1rem;
  background: var(--crc-content-bg, var(--surface-ground));
}

@media (min-width: 640px) {
  .settings-account-section__org-card:not(.settings-account-section__org-card--roles) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.settings-account-section__org-card--wide {
  grid-column: 1 / -1;
}

.settings-account-section__hint {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
}

.settings-account-section__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.settings-account-section__empty-text {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
}

.settings-account-section__footer {
  margin: 0;
  padding-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
  line-height: 1.5;
}
</style>
